import { Analytics } from '@easy-expense/analytics-client';
import { signUp } from '@easy-expense/auth-client';
import {
  useBankConnections,
  useCurrentWorkspace,
  useUserSettings,
  useWorkspaceSettings,
} from '@easy-expense/data-firestore-client';
import { BUSINESS_OWNER_TYPE, TaxProfileSchema } from '@easy-expense/data-schema-v2';
import { getTranslation } from '@easy-expense/intl-client';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import { DoNotFixMeIAmAny } from '@easy-expense/utils-typescript';
import React from 'react';
import ReactPixel from 'react-facebook-pixel';
import { useAuthState } from 'react-firebase-hooks/auth';
import { Country, FlagType, getAllCountries } from 'react-native-country-picker-modal';

import {
  AccountUsageSurvey,
  WorkStartYearSurvey,
  StudentLoanSurvey,
  HousingStatusSurvey,
  DependentsSurvey,
} from './AccountSurveys';
import { BankLink } from './BankLink';
import { BusinessTypeSurvey } from './BusinessTypeSurvey';
import { isValidPhoneNumber, PhoneNumber } from './PhoneNumber';
import { WorkExpenseSurvey } from './WorkExpenseSurvey';
import {
  OnboardingCurrentStep,
  OnboardingFlow,
  OnboardingStep,
} from '../../components/OnboardingFlow/OnboardingFlow';
import { Signup } from '../../components/OnboardingFlow/Signup/Signup';
import { EmailSchema } from '../../components/SignUp.form';
import { auth } from '../../firebase/app';
import { MeteredPaywallActions, useHandleMeteredAction } from '../../hooks/useHandleMeteredAction';
import usePlaidWebLink from '../../hooks/usePlaidWebLink';
import { useUserLocation } from '../../hooks/useUserLocation';
import { createWorkspace } from '../../lib/createWorkspace';
import { CountrySelector, DEFAULT_COUNTRY } from '../ReceiptScanningOnboarding/CountrySelector';
import { DeductionFinder } from '../ReceiptScanningOnboarding/DeductionFinder';
import {
  BusinessRegistrationSurvey,
  IndustrySurvey,
} from '../ReceiptScanningOnboarding/OnboardingSurveys';
import { CATEGORY_LOADING_WAIT } from '../ReceiptScanningOnboarding/ReceiptScannerOnboarding';

export type OnboardingState = OnboardingCurrentStep & {
  teamSurveyOptions: string[];
  businessType: string[];
  industries: string[];
  typeOptions: string[];
  businessRegistration: string[];
  workFromHome: string[];
  businessMeals: string[];
  businessDriving: string[];
  accountUsage: string[];
  workStartYear: string[];
  hasStudentLoans: string[];
  housingStatus: string[];
  hasDependents: string[];
  trialStarted: boolean;
  email?: string;
  emailError?: string;
  password?: string;
  workspaceID?: string;
  countryCode?: string;
  provinceName?: string;
  connectedAccount?: string;
  phoneNumber?: string;
};

const emptyOnboardingState: OnboardingState = {
  currentStepIndex: 0,
  teamSurveyOptions: [],
  businessType: [],
  typeOptions: [],
  industries: [],
  businessRegistration: [],
  workFromHome: [],
  businessMeals: [],
  businessDriving: [],
  accountUsage: [],
  workStartYear: [],
  hasStudentLoans: [],
  housingStatus: [],
  hasDependents: [],
  trialStarted: false,
};

const ConfirmationScreen: React.FC<{ accountName?: string }> = ({ accountName }) => {
  return (
    <Layout.Column>
      <OpenSans.Primary weight="bold-700" size={24}>
        {getTranslation(
          `Congratulations! You've successfully linked ${accountName ?? 'your bank account'}`,
        )}
      </OpenSans.Primary>
      <Spacer.Vertical size={16} />
      <OpenSans.Secondary size={'s-16'}>
        {getTranslation(
          'Our team will review your deductions and get back to you within 24 hours.',
        )}
      </OpenSans.Secondary>
      <Spacer.Vertical size={16} />
      <OpenSans.Secondary size={'s-16'}>
        {getTranslation(
          'In the meantime, you can book a meeting with our team to find all the ways Easy Expense can save you money',
        )}
      </OpenSans.Secondary>
      <Spacer.Vertical size={16} />
      <Layout.Column>
        <a href="https://calendly.com/eli-bucher/30min">
          <OpenSans.Primary size={'s-16'}>{getTranslation('Book a demo')}</OpenSans.Primary>
        </a>
      </Layout.Column>
    </Layout.Column>
  );
};

const BANK_LINK_ONBOARDING_VERSION = 'wob_bank_link_20250526';

export const BankLinkingOnboarding: React.FC = () => {
  const [onboardingState, setOnboardingState] =
    React.useState<OnboardingState>(emptyOnboardingState);
  const { updateWorkspaceSetting } = useWorkspaceSettings();
  const { open, session } = usePlaidWebLink();
  const bankConnections = useBankConnections();
  const handleMeteredLinkAccount = useHandleMeteredAction(
    MeteredPaywallActions.LINK_MULTIPLE_ACCOUNTS,
  );
  const numberOfActiveBankConnections = bankConnections.filter(
    (connection) => connection.status === 'CONNECTED',
  ).length;

  const currentWorkspace = useCurrentWorkspace();
  const userSettings = useUserSettings();
  const { updateUserSetting } = useUserSettings();
  const [country, setCountry] = React.useState<Country>(DEFAULT_COUNTRY);
  const [countryList, setCountryList] = React.useState<Country[]>([]);
  const location = useUserLocation();

  const [user] = useAuthState(auth);

  const userCompletedSignUp = React.useMemo(() => {
    return !user?.isAnonymous;
  }, [user?.isAnonymous]);

  React.useEffect(() => {
    if (session) {
      setOnboardingState({
        ...onboardingState,
        connectedAccount: session?.metadata?.accounts[0]?.meta.name,
        currentStepIndex: onboardingState.currentStepIndex + 1,
      });
    }
  }, [session]);

  React.useEffect(() => {
    getAllCountries(FlagType.EMOJI).then((countries) => {
      setCountryList(countries);
    });
  }, []);

  const validPhoneNumber = React.useMemo(() => {
    return isValidPhoneNumber(onboardingState.phoneNumber ?? '');
  }, [onboardingState.phoneNumber]);

  React.useEffect(() => {
    const locatedCountry = countryList.find((country) => country.cca2 === location?.cca2);
    if (locatedCountry) {
      const { callingCode, name, cca2, region, flag, subregion, currency } = locatedCountry;
      if (cca2 === 'CA' && location?.state) {
        setOnboardingState({ ...onboardingState, provinceName: location.state, countryCode: cca2 });
      } else {
        setOnboardingState({ ...onboardingState, countryCode: cca2 });
      }
      setCountry({
        callingCode,
        cca2,
        name,
        region,
        flag,
        subregion,
        currency,
      });
    }
  }, [location]);

  React.useEffect(() => {
    if (!user) {
      return;
    }
    if (!currentWorkspace) {
      createWorkspaceAsync();
    }
  }, [user]);

  React.useEffect(() => {
    if (!userSettings.data?.settings.onboardingVersion) {
      Analytics.identify(user?.uid ?? null, {
        onboardingVersion: BANK_LINK_ONBOARDING_VERSION,
      });
      updateUserSetting({
        onboardingVersion: BANK_LINK_ONBOARDING_VERSION,
      });
    }
  }, [userSettings]);

  const createWorkspaceAsync = async () => {
    if (currentWorkspace?.key) {
      return;
    }
    await createWorkspace('business', 'Default');

    updateWorkspaceSetting({ ...currentWorkspace?.settings, onboardingType: 'team' });
  };

  const steps: OnboardingStep[] = [
    {
      continueText: 'Continue',
      stepKey: 'business_type',
      canContinue: onboardingState.businessType.length > 0,
      onContinue: () => {
        Analytics.track('onboarding_question_answered', {
          question: 'business_type',
          value: onboardingState.businessType[0] ?? '',
        });

        ReactPixel.trackCustom('Get Started');
        return Promise.resolve(true);
      },
      component: () => (
        <BusinessTypeSurvey
          selectedOptions={onboardingState.businessType}
          setUsageOptions={(businessType: string[]) => {
            setOnboardingState({ ...onboardingState, businessType });
          }}
        />
      ),
    },

    {
      continueText: 'Continue',
      stepKey: 'country',
      canContinue:
        onboardingState.countryCode !== undefined &&
        (onboardingState.countryCode !== 'CA' || onboardingState.provinceName !== undefined),
      component: () => (
        <CountrySelector
          province={onboardingState.provinceName}
          country={country}
          setCountry={(country: Country) => {
            setOnboardingState({ ...onboardingState, countryCode: country.cca2 });
            setCountry(country);
          }}
          setProvince={(provinceName: string) => {
            setOnboardingState({ ...onboardingState, provinceName });
          }}
        />
      ),
    },
    {
      continueText: 'Continue',
      stepKey: 'industry',
      canContinue: onboardingState.industries.length > 0,
      onContinue: () => {
        Analytics.identify(user?.uid ?? null, {
          industry: onboardingState.industries,
        });

        updateWorkspaceSetting({ industry: onboardingState.industries[0] });
        return Promise.resolve(true);
      },
      component: () => (
        <IndustrySurvey
          selectedOptions={onboardingState.industries}
          onSelect={(industries: string[]) => {
            setOnboardingState({ ...onboardingState, industries });
          }}
        />
      ),
    },
    {
      continueText: 'Continue',
      stepKey: 'business_registration',
      canContinue: onboardingState.businessRegistration.length > 0,
      onContinue: () => {
        Analytics.track('onboarding_question_answered', {
          question: 'business_registration',
          value: onboardingState.businessRegistration[0] ?? '',
        });

        updateWorkspaceSetting({
          businessRegistration: BUSINESS_OWNER_TYPE.parse(onboardingState.businessRegistration[0]),
        });
        return Promise.resolve(true);
      },
      component: () => (
        <BusinessRegistrationSurvey
          businessRegistration={onboardingState.businessRegistration}
          setBusinessRegistration={(businessRegistration: string[]) => {
            setOnboardingState({ ...onboardingState, businessRegistration });
          }}
          countryCode={country.cca2}
        />
      ),
    },
    {
      continueText: 'Continue',
      stepKey: 'work_from_home',
      canContinue: onboardingState.workFromHome.length > 0,
      onContinue: () => {
        Analytics.track('onboarding_question_answered', {
          question: 'work_from_home',
          value: onboardingState.workFromHome[0] ?? '',
        });
        const currentTaxProfile = userSettings.data?.settings.taxProfile;
        const worksFromHome = onboardingState.workFromHome[0] === 'Yes';
        const businessType = onboardingState.businessType[0] ?? '';

        updateUserSetting({
          taxProfile: TaxProfileSchema.parse({
            ...currentTaxProfile,
            worksFromHome,
            businessType,
          }),
        });
        return Promise.resolve(true);
      },
      component: () => (
        <WorkExpenseSurvey
          type="home"
          selectedOptions={onboardingState.workFromHome}
          onSelect={(workFromHome: string[]) => {
            setOnboardingState({ ...onboardingState, workFromHome });
          }}
        />
      ),
    },
    {
      continueText: 'Continue',
      stepKey: 'business_meals',
      canContinue: onboardingState.businessMeals.length > 0,
      onContinue: () => {
        Analytics.track('onboarding_question_answered', {
          question: 'business_meals',
          value: onboardingState.businessMeals[0] ?? '',
        });
        const currentTaxProfile = userSettings.data?.settings.taxProfile;
        const businessMeals = onboardingState.businessMeals[0] === 'Yes';
        updateUserSetting({
          taxProfile: TaxProfileSchema.parse({
            ...currentTaxProfile,
            businessMeals,
          }),
        });
        return Promise.resolve(true);
      },
      component: () => (
        <WorkExpenseSurvey
          type="meals"
          selectedOptions={onboardingState.businessMeals}
          onSelect={(businessMeals: string[]) => {
            setOnboardingState({ ...onboardingState, businessMeals });
          }}
        />
      ),
    },
    {
      continueText: 'Continue',
      stepKey: 'business_driving',
      canContinue: onboardingState.businessDriving.length > 0,
      loadingText: getTranslation('Finding your Deductions...'),
      loadingComponent: () => <DeductionFinder />,
      onContinue: async () => {
        Analytics.track('onboarding_question_answered', {
          question: 'drive_for_business',
          value: onboardingState.businessDriving[0] ?? '',
        });
        const currentTaxProfile = userSettings.data?.settings.taxProfile;
        const drivesForBusiness = onboardingState.businessDriving[0] === 'Yes';
        updateUserSetting({
          taxProfile: TaxProfileSchema.parse({
            ...currentTaxProfile,
            drivesForBusiness,
          }),
        });
        return await new Promise((resolve) => {
          setTimeout(() => {
            resolve(true);
          }, CATEGORY_LOADING_WAIT);
        });
      },
      component: () => (
        <WorkExpenseSurvey
          type="drive"
          selectedOptions={onboardingState.businessDriving}
          onSelect={(businessDriving: string[]) => {
            setOnboardingState({ ...onboardingState, businessDriving });
          }}
        />
      ),
    },
    {
      continueText: userCompletedSignUp ? 'Continue' : 'Sign Up',
      stepKey: 'sign_up',
      onContinue: async () => {
        if (userCompletedSignUp) {
          return true;
        }

        try {
          await signUp(onboardingState.email ?? '', onboardingState.password ?? '');
          ReactPixel.trackCustom('User Signed Up');
          return true;
        } catch (error: DoNotFixMeIAmAny) {
          if (error.code === 'auth/wrong-password' || error.code === 'auth/email-already-in-use') {
            setOnboardingState({
              ...onboardingState,
              emailError: 'An account already exists with this email.',
            });
          } else {
            setOnboardingState({
              ...onboardingState,
              emailError: 'Cannot log in. Please contact support.',
            });
          }
        }

        return false;
      },
      canContinue:
        userCompletedSignUp ||
        (onboardingState.email !== undefined &&
          onboardingState.password !== undefined &&
          onboardingState.password.length >= 8 &&
          EmailSchema.safeParse(onboardingState.email).success),
      component: () => (
        <Signup
          setEmail={(email: string) => {
            setOnboardingState({ ...onboardingState, email });
          }}
          setPassword={(password: string) => {
            setOnboardingState({ ...onboardingState, password });
          }}
          onboardingState={onboardingState}
        />
      ),
    },
    {
      continueText: 'Continue',
      stepKey: 'phone_number',
      canContinue: validPhoneNumber,
      onContinue: async () => {
        updateUserSetting({ verifiedPhoneNumber: onboardingState.phoneNumber });
        return true;
      },
      component: () => (
        <PhoneNumber
          phoneNumber={onboardingState.phoneNumber}
          setPhoneNumber={(phoneNumber: string) => {
            setOnboardingState({ ...onboardingState, phoneNumber });
          }}
        />
      ),
    },
    {
      continueText: session ? 'Continue' : 'Connect Bank',
      stepKey: 'plaid_link',
      onContinue: () => {
        if (session) {
          ReactPixel.trackCustom('Bank Link Completed');
          return Promise.resolve(true);
        } else {
          ReactPixel.trackCustom('Bank Link Clicked');
          if (numberOfActiveBankConnections > 0) {
            handleMeteredLinkAccount(() => open());
          } else {
            open();
          }
          return Promise.resolve(false);
        }
      },
      canContinue: true,
      component: () => <BankLink />,
    },
    {
      continueText: 'Continue',
      stepKey: 'account_usage',
      canContinue: onboardingState.accountUsage.length > 0,
      onContinue: () => {
        const accountUsageType = onboardingState.accountUsage[0] as
          | 'All Business'
          | 'Mostly Business'
          | 'Mixed'
          | 'Mostly Personal'
          | 'All Personal';
        Analytics.track('bank_profile_answered', {
          question: 'account_usage',
          value: accountUsageType,
        });
        const currentTaxProfile = userSettings.data?.settings.taxProfile;
        updateUserSetting({
          taxProfile: TaxProfileSchema.parse({
            ...currentTaxProfile,
            accountUsageType,
          }),
        });
        return Promise.resolve(true);
      },
      component: () => (
        <AccountUsageSurvey
          connectedAccount={onboardingState.connectedAccount}
          selectedOptions={onboardingState.accountUsage}
          onSelect={(accountUsage: string[]) => {
            setOnboardingState({ ...onboardingState, accountUsage });
          }}
        />
      ),
    },
    {
      continueText: 'Continue',
      stepKey: 'work_start_year',
      canContinue: onboardingState.workStartYear.length > 0,
      onContinue: () => {
        Analytics.track('bank_profile_answered', {
          question: 'work_start_year',
          value: onboardingState.workStartYear[0] ?? '',
        });
        const currentTaxProfile = userSettings.data?.settings.taxProfile;
        updateUserSetting({
          taxProfile: TaxProfileSchema.parse({
            ...currentTaxProfile,
            workStartYear: parseInt(onboardingState.workStartYear[0] ?? '2023', 10),
          }),
        });
        return Promise.resolve(true);
      },
      component: () => (
        <WorkStartYearSurvey
          selectedOptions={onboardingState.workStartYear}
          onSelect={(workStartYear: string[]) => {
            setOnboardingState({ ...onboardingState, workStartYear });
          }}
        />
      ),
    },

    {
      continueText: 'Continue',
      stepKey: 'student_loans',
      canContinue: onboardingState.hasStudentLoans.length > 0,
      onContinue: () => {
        const hasStudentLoans = onboardingState.hasStudentLoans[0] === 'Yes';
        Analytics.track('bank_profile_answered', {
          question: 'student_loans',
          value: hasStudentLoans,
        });
        const currentTaxProfile = userSettings.data?.settings.taxProfile;
        updateUserSetting({
          taxProfile: TaxProfileSchema.parse({
            ...currentTaxProfile,
            hasStudentLoans,
          }),
        });
        return Promise.resolve(true);
      },
      component: () => (
        <StudentLoanSurvey
          selectedOptions={onboardingState.hasStudentLoans}
          onSelect={(hasStudentLoans: string[]) => {
            setOnboardingState({ ...onboardingState, hasStudentLoans });
          }}
        />
      ),
    },
    {
      continueText: 'Continue',
      stepKey: 'housing_status',
      canContinue: onboardingState.housingStatus.length > 0,
      onContinue: () => {
        const housingStatus = onboardingState.housingStatus[0] as
          | 'Home Owner'
          | 'Renter'
          | 'Both'
          | 'Neither';
        Analytics.track('bank_profile_answered', {
          question: 'housing_status',
          value: housingStatus,
        });
        const currentTaxProfile = userSettings.data?.settings.taxProfile;
        updateUserSetting({
          taxProfile: TaxProfileSchema.parse({
            ...currentTaxProfile,
            housingStatus,
          }),
        });
        return Promise.resolve(true);
      },
      component: () => (
        <HousingStatusSurvey
          selectedOptions={onboardingState.housingStatus}
          onSelect={(housingStatus: string[]) => {
            setOnboardingState({ ...onboardingState, housingStatus });
          }}
        />
      ),
    },
    {
      continueText: 'Continue',
      stepKey: 'dependents',
      canContinue: onboardingState.hasDependents.length > 0,
      onContinue: () => {
        const hasDependents = onboardingState.hasDependents[0] === 'Yes';
        Analytics.track('bank_profile_answered', {
          question: 'dependents',
          value: hasDependents,
        });
        const currentTaxProfile = userSettings.data?.settings.taxProfile;
        updateUserSetting({
          taxProfile: TaxProfileSchema.parse({
            ...currentTaxProfile,
            hasDependents,
          }),
        });
        return Promise.resolve(true);
      },
      component: () => (
        <DependentsSurvey
          selectedOptions={onboardingState.hasDependents}
          onSelect={(hasDependents: string[]) => {
            setOnboardingState({ ...onboardingState, hasDependents });
          }}
        />
      ),
    },
    {
      continueText: '',
      showContinue: false,
      stepKey: 'confirmation',
      canContinue: true,
      component: () => <ConfirmationScreen accountName={onboardingState.connectedAccount} />,
    },
  ];

  return (
    <OnboardingFlow
      onboardingState={onboardingState}
      setOnboardingState={setOnboardingState}
      onboardingSteps={steps}
      analyticsKey="bank_link"
      canGoToFirstStep={true}
    />
  );
};
